import { Trans, useLingui } from "@lingui/react/macro";
import { UploadIcon, XIcon } from "lucide-react";
import { useId, useState } from "react";

import { showToast } from "./toasts";

export function FileUploadDropZone({
  onSelect,
  accept,
  multiple = false,
  error = false,
  flaggedNames,
}: {
  onSelect?: (files: File[]) => void;
  accept?: string[];
  multiple?: boolean;
  error?: boolean;
  /** Names of selected files the caller wants marked as rejected. */
  flaggedNames?: string[];
}) {
  const inputId = useId();
  const { t } = useLingui();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const selectFiles = (files: File[]) => {
    setSelectedFiles(files);
    onSelect?.(files);
  };

  const validateAndSelect = (files: File[]) => {
    if (files.length === 0) return;

    if (!multiple && files.length > 1) {
      showToast({ message: t`You can only upload one file at a time` });
      return;
    }

    if (accept) {
      const isValid = (file: File) =>
        accept.some((ext) =>
          file.name.toLowerCase().endsWith(ext.toLowerCase()),
        );
      const invalid = files.filter((file) => !isValid(file));
      if (invalid.length > 0) {
        const skipped = invalid.map((file) => file.name).join(", ");
        const accepted = accept.join(", ");
        showToast({
          message: t`Skipped ${skipped}. Only the following types are accepted: ${accepted}`,
        });
        files = files.filter(isValid);
        if (files.length === 0) return;
      }
    }

    if (multiple) {
      const merged = [...selectedFiles];
      for (const file of files) {
        if (!merged.some((f) => fileKey(f) === fileKey(file)))
          merged.push(file);
      }
      selectFiles(merged);
    } else {
      selectFiles(files);
    }
  };

  return (
    <div
      className={`flex w-[375px] flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed p-5 text-center text-sm transition-colors ${
        dragOver
          ? "border-info-border bg-info-soft text-info-foreground"
          : error
            ? "border-danger-border bg-danger-soft text-danger-foreground"
            : "border-border bg-muted text-muted-foreground"
      }`}
      onDrop={(event) => {
        event.preventDefault();
        setDragOver(false);

        const files: File[] = [];

        if (event.dataTransfer.items) {
          for (const item of event.dataTransfer.items) {
            if (item.kind === "file") {
              const file = item.getAsFile();
              if (file) files.push(file);
            }
          }
        } else {
          files.push(...Array.from(event.dataTransfer.files));
        }

        validateAndSelect(files);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={(event) => {
        if (
          !(event.relatedTarget instanceof HTMLElement) ||
          !event.currentTarget.contains(event.relatedTarget)
        ) {
          setDragOver(false);
        }
      }}
    >
      <UploadIcon size={28} className="text-muted-foreground" aria-hidden />

      {selectedFiles.length === 0 && (
        <div>
          <Trans>
            Drag and drop {multiple ? t`files` : t`a file`} here, or click below
          </Trans>
        </div>
      )}

      {selectedFiles.length > 0 && (
        <ul className="w-full space-y-0.5 text-left">
          {selectedFiles.map((file) => {
            const flagged = flaggedNames?.includes(file.name);
            return (
              <li
                key={fileKey(file)}
                className={`flex min-w-0 items-center gap-2 ${
                  flagged ? "text-danger-foreground" : ""
                }`}
              >
                <span className="text-muted-foreground">
                  <Trans>Selected:</Trans>{" "}
                </span>
                <strong className="flex-1 truncate">{file.name}</strong>
                {flagged && (
                  <span className="shrink-0 text-xs font-medium">
                    <Trans>already uploaded</Trans>
                  </span>
                )}
                <button
                  type="button"
                  aria-label={t`Remove ${file.name}`}
                  className="text-danger-foreground hover:text-danger-foreground/80 shrink-0 cursor-pointer bg-transparent p-0"
                  onClick={() =>
                    selectFiles(selectedFiles.filter((f) => f !== file))
                  }
                >
                  <XIcon size={18} />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <label
        htmlFor={inputId}
        className="border-input bg-secondary text-secondary-foreground hover:bg-secondary/80 inline-flex h-8 cursor-pointer items-center justify-center rounded-md border px-2.5 text-sm font-medium transition-colors"
      >
        <Trans>Select</Trans>
      </label>

      <input
        id={inputId}
        className="hidden"
        type="file"
        multiple={multiple}
        onChange={(event) => {
          if (event.target.files && event.target.files.length > 0) {
            validateAndSelect(Array.from(event.target.files));
          }
          event.target.value = "";
        }}
        accept={accept ? accept.join(",") : undefined}
      />
    </div>
  );
}

const fileKey = (file: File) =>
  `${file.name}:${file.size}:${file.lastModified}`;
